import { createContext, useContext, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserLocation } from '../components/charts/ChartsSlice';

// TODO: add a value to track if we have location permission, if not we can display a UI warning
const LocationContext = createContext();

export function LocationProvider({ children }) {
    const [location, setLocation] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();
    const chartSettings = useSelector(state => state.chartSettings.chartSettings);
    const showUserLocation = chartSettings.showUserLocation;
    useEffect(() => {
        // console.log('showUserLocation', showUserLocation)
        if (!showUserLocation) {
            setLocation(null);
            setError(null);
            setIsLoading(false);
            return;
        }

        if (!navigator.geolocation) {
            setError('Geolocation is not supported by your browser');
            setIsLoading(false);
            return;
        }

        const options = {
            enableHighAccuracy: true,
            maximumAge: 0
        };

        const success = (position) => {
            const newLocation = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: position.timestamp,
            };
            setLocation(newLocation);
            dispatch(updateUserLocation({ newLocation, chartSettings }));
            setIsLoading(false);
        };

        const error = (err) => {
            setError(err.message);
            setIsLoading(false);
        };

        // Get initial position
        navigator.geolocation.getCurrentPosition(success, error, options);

        // Watch for position changes
        const watchId = navigator.geolocation.watchPosition(success, error, options);

        return () => {
            navigator.geolocation.clearWatch(watchId);
        };
    }, [dispatch, chartSettings, showUserLocation]);

    const value = {
        location,
        error,
        isLoading,
        getCurrentLocation: () => {
            return new Promise((resolve, reject) => {
                if (!showUserLocation) {
                    reject(new Error('Location tracking is disabled'));
                    return;
                }

                if (!navigator.geolocation) {
                    reject(new Error('Geolocation is not supported by your browser'));
                    return;
                }

                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const newLocation = {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy,
                            timestamp: position.timestamp,
                        };
                        setLocation(newLocation);
                        dispatch(updateUserLocation({ newLocation, chartSettings }));
                        resolve(newLocation);
                    },
                    (err) => {
                        setError(err.message);
                        reject(err);
                    },
                    {
                        enableHighAccuracy: true,
                        maximumAge: 0
                    }
                );
            });
        }
    };

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
}

export function useUserLocation() {
    const context = useContext(LocationContext);
    if (!context) {
        throw new Error('useUserLocation must be used within a LocationProvider');
    }
    return context;
} 